import { useEffect, useRef, useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { MEDIA_RESOURCE_TYPE } from '../CreatePageConstants';
import { getMediaResolutionUrl } from '../helpers';
import { createActions } from '../../CreatePage/store';

const styles = () => ({
    videoElement: {
        display: 'none'
    }
});

export var BackgroundVideoElementInstance = null;

const BackgroundVideoElement = memo(({
    classes,
    backgroundMediaSource,
    previewResolution,
    playingPreview,
    freezeVideoBackground,
    optimizationModeEnabled,
    setPageProperty,
    updateBackgroundSettings
}) => {
    const videoRef = useRef(null);
    const prevOptimizationModeRef = useRef(optimizationModeEnabled);

    useEffect(() => {
        BackgroundVideoElementInstance = videoRef.current;
        return () => BackgroundVideoElementInstance = null;
    }, []);

    useEffect(() => {
        const isVideoBackground = backgroundMediaSource && backgroundMediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO;
        if (isVideoBackground)
            updateVideoPlayback();
    }, [playingPreview, freezeVideoBackground, previewResolution, backgroundMediaSource]);

    // An Optimization Mode toggle recreates the Pixi renderer, and the new background texture
    // starts empty until it can sample a decoded frame from this element.
    // Playing for an instant and pausing again decodes one fresh frame for the new texture.
    useEffect(() => {
        if (prevOptimizationModeRef.current === optimizationModeEnabled) return;
        prevOptimizationModeRef.current = optimizationModeEnabled;

        const videoEl = videoRef.current;
        const isVideoBackground = backgroundMediaSource && backgroundMediaSource.mediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO;
        if (!videoEl || !isVideoBackground || !videoEl.paused) return;

        videoEl.play()
            .then(() => videoEl.pause())
            .catch(() => {});
    }, [optimizationModeEnabled, backgroundMediaSource]);

    const updateVideoPlayback = useCallback(() => {
        if (!BackgroundVideoElementInstance) return;

        if (playingPreview && !freezeVideoBackground)
            BackgroundVideoElementInstance.play().catch(error => console.error('Failed to play video:', error));
        else BackgroundVideoElementInstance.pause();
    }, [playingPreview, freezeVideoBackground]);

    const mediaResourceType = backgroundMediaSource ? backgroundMediaSource.mediaResourceType : null;
    const src = (mediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO) ? getMediaResolutionUrl(backgroundMediaSource.data, previewResolution) : null;

    useEffect(() => {
        if (!videoRef.current || !src) return;
        videoRef.current.load();
        videoRef.current.currentTime = 0.001;
    }, [src]);

    const onLoadedData = useCallback(() => {
        setPageProperty('backgroundVideosLoading', false);
        if (videoRef.current) {
            setPageProperty('backgroundVideoDuration', videoRef.current.duration || 0);
        }
        updateVideoPlayback();
        if (updateBackgroundSettings) {
            // System triggered load, refresh background without marking project unsaved.
            updateBackgroundSettings({}, true);
        }
    }, [setPageProperty, updateVideoPlayback, updateBackgroundSettings]);

    const onLoadStart = useCallback(() => {
        setPageProperty('backgroundVideosLoading', true);
    }, [setPageProperty]);

    const onError = useCallback(() => {
        setPageProperty('backgroundVideosLoading', false);
    }, [setPageProperty]);

    return (
        <video
            onLoadStart={onLoadStart}
            onError={onError}
            onLoadedData={onLoadedData}
            className={classes.videoElement}
            crossOrigin='anonymous'
            preload='none'
            muted
            loop
            ref={videoRef}
            playsInline
            src={src}
            type='video/mp4'
        />
    );
});

const mapStateToProps = (state) => {
    const { mediaSource: backgroundMediaSource } = state.createPreview.present.videoSettings.background;
    const { freezeVideoBackground, optimizationModeEnabled } = state.create;

    const {
        playingPreview,
        previewResolution
    } = state.create;

    return {
        backgroundMediaSource,
        playingPreview,
        previewResolution,
        // Optimization Mode implies frozen video backgrounds
        freezeVideoBackground: freezeVideoBackground || optimizationModeEnabled,
        optimizationModeEnabled
    };
};

const mapDispatchToProps = (dispatch) => {
    const setPageProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const updateBackgroundSettings = (properties, initializationProcess) => dispatch(createActions.updateBackgroundSettings(properties, initializationProcess));

    return {
        setPageProperty,
        updateBackgroundSettings
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(BackgroundVideoElement, styles));